import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usePageData } from '@/contexts/pageContext';
import { CartNotification } from '@/components/CartNotification';
import { NotificationsQueue } from '@/components/Notifications';
import { Endpoint, QueryKey } from '@/constants';
import type { OrderItem, Product } from '@/types';
import { postData } from '@/utils/postData';
import { useDataLayer } from '@/hooks/useDataLayer';
import {
  getQueryId,
  getAuthUserToken,
  addProductAndQueryToCookie,
  setBasketAddition,
} from '@/utils/searchAnalytics';

export const useAddToBasket = (product?: Product, listName?: string) => {
  const queryClient = useQueryClient();
  const { urls } = usePageData();
  const { addToCart } = useDataLayer();
  const { isPending, mutate } = useMutation({
    mutationFn: postData(Endpoint.AddToBasket),
    onSuccess: (data, variables) => {
      const {
        isSuccess,
        messages: [heading, body],
      } = data;
      const successMessage = { heading, body };

      queryClient.invalidateQueries({ queryKey: [QueryKey.CustomerStatus] });
      queryClient.invalidateQueries({ queryKey: [QueryKey.GetCart] });

      // Extract the product code and quantity from the request variables
      const productCode = isSuccess && typeof variables === 'object' && variables !== null && 'code' in variables ? String(variables.code) : null;
      const requestedQuantity = typeof variables === 'object' && variables !== null && 'quantity' in variables ? Number(variables.quantity) || 1 : 1;

      // Track add to cart event in GTM DataLayer (GA4)
      if (isSuccess && productCode && data.data?.items) {
        const addedItem = data.data.items.find(
          (item: OrderItem) => item.sku === productCode,
        );

        if (addedItem) {
          addToCart(
            addedItem,
            requestedQuantity,
            listName,
            product?.price?.amount,
          );
        }
      }

      // Algolia insight: "Add to Basket" event — fires alongside GA4 addToCart
      // Mirrors _AlgoliaInsightEvents.cshtml addBasketGtmEventFromReact
      if (isSuccess && productCode) {
        const price = product?.price?.amount ?? 0;
        const queryId = getQueryId() ?? '';
        const eventName = queryId.length > 0
          ? 'Add to Basket with Search Clicked'
          : 'Add to Basket Clicked';

        window.dataLayer?.push({
          event: eventName,
          authUserToken: getAuthUserToken(),
          queryId,
          objectIds: [productCode],
          objectData: [{ price: Number(price.toFixed(2)), quantity: requestedQuantity }],
          value: Number((requestedQuantity * price).toFixed(2)),
          currency: 'EUR',
        });

        if (queryId.length > 0) {
          addProductAndQueryToCookie(productCode, queryId);
        }
        setBasketAddition();
      }

      const notification = product ? (
        <CartNotification
          isError={!isSuccess}
          message={successMessage}
          product={product}
          url={urls.basket}
        />
      ) : null;

      // Only enqueue a toast when there's something to show. The toast region always renders its
      // box + close (X) button, so adding an empty (null) notification produces a broken
      // "small rectangle with an X" with no content. Callers without a product still get the
      // mini-cart update via the query invalidations above.
      if (notification) {
        NotificationsQueue.add(
          { notification },
          isSuccess ? { timeout: 5000 } : undefined,
        );
      }
    },
  });

  return { addToBasket: mutate, isPending };
};
